import { Linking, Share, Platform, NativeModules } from "react-native";
import { z } from "zod";
import { faBan, faClock } from "@fortawesome/free-solid-svg-icons";
import { STRINGS, COLORS, errorMessages } from "../constants";

/**
 * Opens Google Maps with the provided latitude and longitude.
 *
 * @param {number|string} latitude - The latitude coordinate. Can be a number or a string that can be parsed to a float.
 * @param {number|string} longitude - The longitude coordinate. Can be a number or a string that can be parsed to a float.
 * @returns {void}
 */
export const openDirections = (latitude, longitude) => {
  latitude = parseFloat(latitude); // Convert to a float
  longitude = parseFloat(longitude); // Remove any non-numeric characters

  if (!isNaN(latitude) && !isNaN(longitude)) {
    Linking.openURL(googleMapLink(latitude, longitude)).catch((err) =>
      console.error("Failed to open Google Maps", err)
    );
  } else {
    console.error("Invalid latitude or longitude");
  }
};

/**
 * Shares a message with a given subject using the Share API.
 *
 * @param {string} msg - The message to be shared.
 * @param {string} segment_name - The subject of the message.
 * @returns {Promise<void>} A promise that resolves when the share action is complete.
 */
export const share = async (msg, segment_name) => {
  await Share.share({ message: msg }, { subject: segment_name }).catch(() => console.log(STRINGS.shareCancelled));
};

/**
 * Generates a Google Maps link for driving directions to the specified latitude and longitude.
 *
 * @param {number} latitude - The latitude of the destination.
 * @param {number} longitude - The longitude of the destination.
 * @returns {string} The Google Maps link for driving directions.
 */
export const googleMapLink = (latitude, longitude) => {
  return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
};

/**
 * Determines the open seasons and returns the corresponding status, icon, and styles.
 * @param {object} attributes - The object containing seasonal hours.
 * @returns {object} An object containing the chip text, icon, background color, border color, and icon color.
 */
export const getOpenSeasons = (attributes) => {
  const openSeasons = [];

  if (attributes.winterhours?.trim().toLowerCase() !== "closed") openSeasons.push(STRINGS.winter);
  if (attributes.springhours?.trim().toLowerCase() !== "closed") openSeasons.push(STRINGS.spring);
  if (attributes.summerhours?.trim().toLowerCase() !== "closed") openSeasons.push(STRINGS.summer);

  const openStatus = openSeasons.length > 0 ? `${STRINGS.openDuring} ${openSeasons.join(", ")}` : STRINGS.closed;

  return {
    chipText: openStatus,
    chipIcon: openStatus === STRINGS.closed ? faBan : faClock,
    chipBackgroundColor: openStatus === STRINGS.closed ? COLORS.errorLight : COLORS.backgroundGreen,
    chipBorderColor: openStatus === STRINGS.closed ? COLORS.error : COLORS.primary,
    chipIconColor: openStatus === STRINGS.closed ? COLORS.error : COLORS.primary,
  };
};

/**
 * Sanitizes the input text by validating it against specific rules and formatting it.
 *
 * Validation rules:
 * - Must be a string with a minimum length of 2 and a maximum length of 100.
 * - Can only contain alphanumeric characters, spaces, "&", "-", ".", ",", ":", and "/".
 * - Must have at least 2 non-space characters.
 *
 * Formatting:
 * - Trims leading and trailing spaces.
 * - Converts text to lowercase.
 * - Normalizes the text to remove diacritical marks (e.g., accents).
 *
 * @param {string} text - The input text to sanitize.
 * @returns {{ pass: boolean, text?: string }} An object containing:
 *   - `pass`: A boolean indicating whether the input text passed validation.
 *   - `text`: The sanitized text if validation passed, otherwise undefined.
 *   - `errors`: An array of validation errors if validation failed.
 */
export const verifySearchText = (text) => {
  const checkText = z
    .string({
      required_error: errorMessages.textTooShort,
      invalid_type_error: errorMessages.textTooShort,
    })
    .min(3, { message: errorMessages.textTooShort })
    .max(100, { message: errorMessages.textTooLong })
    // .regex(/^[a-zA-Z0-9 &\-.,:/]*$/, {
    //   message: errorMessages.invalidCharacters,
    // }) // Currently commented out to be revisited later
    .refine((val) => !/^\s{1,3}$/.test(val) && val.trim().length >= 3, {
      message: errorMessages.textTooShort,
    })
    .transform((val) => val.trim().toLowerCase());

  if (text.length === 0) {
    return { pass: false, errors: [errorMessages.textTooShort] };
  } else {
    try {
      checkText.parse(text);
      return {
        pass: true,
        text: text
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .trim(),
      };
    } catch (error) {
      const errors =
        error.errors && Array.isArray(error.errors)
          ? [...new Set(error.errors.map((err) => err.message))]
          : [errorMessages.textTooShort];
      return { pass: false, errors: errors };
    }
  }
};
/**
 * Returns the appropriate marker icon image for a given parking type and state.
 * Supported types:
 * - "meter"
 * - "structure"
 * - fallback: "default"
 *
 * @param {string} type - The type of parking location (e.g., "meter", "structure").
 * @param {"enabled" | "disabled"} state - The marker state.
 * @returns {*} The required image asset for the marker icon.
 */
export const getMarkerIcon = (type = "", color = "gray", state = "disabled") => {
  color = "gray"; // Temporarily disable parking reporting
  const icons = {
    meter:
      Platform.OS === "android"
        ? {
            enabled: {
              green: { uri: "meterparkingenabledgreen" },
              yellow: { uri: "meterparkingenabledyellow" },
              red: { uri: "meterparkingenabledred" },
              gray: { uri: "meterparkingenabled" },
            },
            disabled: {
              green: { uri: "meterparkingdisabledgreen" },
              yellow: { uri: "meterparkingdisabledyellow" },
              red: { uri: "meterparkingdisabledred" },
              gray: { uri: "meterparkingdisabled" },
            },
          }
        : {
            enabled: {
              green: require("../assets/images/meter-parking-enabled_green.png"),
              yellow: require("../assets/images/meter-parking-enabled_yellow.png"),
              red: require("../assets/images/meter-parking-enabled_red.png"),
              gray: require("../assets/images/meter-parking-enabled.png"),
            },
            disabled: {
              green: require("../assets/images/meter-parking-disabled_green.png"),
              yellow: require("../assets/images/meter-parking-disabled_yellow.png"),
              red: require("../assets/images/meter-parking-disabled_red.png"),
              gray: require("../assets/images/meter-parking-disabled.png"),
            },
          },

    structure:
      Platform.OS === "android"
        ? {
            enabled: {
              green: { uri: "structureparkingenabledgreen" },
              yellow: { uri: "structureparkingenabledyellow" },
              red: { uri: "structureparkingenabledred" },
              gray: { uri: "structureparkingenabled" },
            },
            disabled: {
              green: { uri: "structureparkingdisabledgreen" },
              yellow: { uri: "structureparkingdisabledyellow" },
              red: { uri: "structureparkingdisabledred" },
              gray: { uri: "structureparkingdisabled" },
            },
          }
        : {
            enabled: {
              green: require("../assets/images/structure-parking-enabled_green.png"),
              yellow: require("../assets/images/structure-parking-enabled_yellow.png"),
              red: require("../assets/images/structure-parking-enabled_red.png"),
              gray: require("../assets/images/structure-parking-enabled.png"),
            },
            disabled: {
              green: require("../assets/images/structure-parking-disabled_green.png"),
              yellow: require("../assets/images/structure-parking-disabled_yellow.png"),
              red: require("../assets/images/structure-parking-disabled_red.png"),
              gray: require("../assets/images/structure-parking-disabled.png"),
            },
          },

    default:
      Platform.OS === "android"
        ? {
            enabled: { uri: `parkingpointenabled` },
            disabled: { uri: `parkingpointdisabled` },
          }
        : {
            enabled: require(`../assets/images/parking-point-enabled.png`),
            disabled: require(`../assets/images/parking-point-disabled.png`),
          },
  };

  return (icons[type] || icons.default)[state][color];
};

/**
 * Checks if the current platform supports the Share API.
 *
 * @returns {boolean} True if sharing is supported, false otherwise.
 */
export const supportsShare = () => {
  if (Platform.OS === "web") {
    return navigator.share !== undefined;
  } else {
    return true; // Assume sharing is supported on mobile platforms
  }
};
// Utility to detect mobile browsers
export const isMobileWeb = () => {
  if (typeof window === "undefined") return false;
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|iphone|ipad|ipod/i.test(userAgent);
};

/**
 * Filters and formats a list of event objects to extract highlighted events in the "Concerts" category.
 *
 * @param {Array<Object>} filteredEvents - The array of event objects to filter and format.
 * @returns {Object} An object containing arrays of event names, start dates, locations, images, and sort keys
 *                   for the highlighted events.
 * @property {Array<string>} name - Names of the highlighted events.
 * @property {Array<string>} startDate - Start dates of the highlighted events.
 * @property {Array<Object>} images - First image object of each highlighted event.
 * @property {Array<any>} occurrence_id - Sort keys of the highlighted events.
 */
export const createHighlightedEvents = (featuredEvents) => {
  const name = [];
  const startDate = [];
  const images = [];
  const occurrence_id = [];

  for (const item of featuredEvents) {
    if (item.name && item.occurrences?.[0]?.formatted?.startDate && item.images?.[0] && item.occurrence_id) {
      name.push(item.name);
      startDate.push(item.occurrences[0].formatted.startDate);
      images.push(item.images[0]);
      occurrence_id.push(item.occurrence_id);
    }
  }

  return { name, startDate, images, occurrence_id };
};

/**
 * Converts a UTC date string to a local Date object with the same year, month, and day.
 *
 * @param {string} dateString - The UTC date string to convert.
 * @returns {Date} A Date object representing the local date (year, month, day).
 */
export const parseUTCToLocal = (dateString) => {
  const d = new Date(dateString);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * Adds an event to the user's calendar based on the platform (iOS, Android, or Web).
 *
 * @async
 * @function
 * @param {string} name - The name/title of the event.
 * @param {Date|string} start - The start date and time of the event.
 * @param {Date|string} end - The end date and time of the event.
 * @param {string} address - The location/address of the event.
 * @param {string} [description=""] - A description of the event (optional).
 * @param {string} [url=""] - A URL associated with the event (optional).
 * @returns {Promise<void>} Resolves when the event has been added to the calendar.
 */
export const addToCalendar = async (name, start, end, address, description = "", url = "") => {
  const event = {
    name,
    start,
    end,
    address,
    description,
    url,
  };

  switch (Platform.OS) {
    case "ios":
      createCalendarEventiOS(event);
      break;
    case "android":
      createCalendarEventAndroid(event);
      break;
    default:
      createCalendarEventWeb(event);
  }
};

/**
 * Creates a calendar event on iOS devices using the native CalendarModule.
 *
 * @async
 * @function createCalendarEventiOS
 * @param {Object} event - The event details.
 * @param {string} event.name - The name/title of the event.
 * @param {string|number} event.start - The start time of the event (format depends on native module).
 * @param {string|number} event.end - The end time of the event (format depends on native module).
 * @param {string} event.address - The address/location of the event.
 * @param {string} event.description - The description of the event.
 * @param {string} event.url - The URL associated with the event.
 * @returns {Promise<void>} Resolves when the event is added or logs an error if permission is not given.
 */
const createCalendarEventiOS = async (event) => {
  const { CalendarModule } = NativeModules;
  try {
    await CalendarModule.addEvent(
      event.name,
      `${event.start}.000Z`.replace(" ", "T"),
      `${event.end}.000Z`.replace(" ", "T"),
      event.address,
      event.description,
      event.url
    );
  } catch (error) {
    console.log("Permission not given", error);
  }
};

/**
 * Creates a calendar event on Android devices using the native CalendarModule.
 *
 * @param {Object} event - The event details.
 * @param {string} event.name - The name/title of the event.
 * @param {string} event.description - The description of the event.
 * @param {string} event.address - The location/address of the event.
 * @param {string|Date} event.start - The start date and time of the event.
 * @param {string|Date} event.end - The end date and time of the event.
 * @returns {void}
 */
const createCalendarEventAndroid = (event) => {
  const startMillis = new Date(event.start).getTime();
  const endMillis = new Date(event.end).getTime();
  const allDay = startMillis === endMillis;

  NativeModules.CalendarModule.addEvent(event.name, event.description, event.address, startMillis, endMillis, allDay);
};

/**
 * Generates and triggers a download of an ICS (iCalendar) file for the given event.
 *
 * @param {Object} event - The event details.
 * @param {string|Date} event.start - The start date/time of the event.
 * @param {string|Date} event.end - The end date/time of the event.
 * @param {string} event.name - The name or summary of the event.
 * @param {string} event.description - The HTML description of the event.
 * @param {string} event.address - The location/address of the event.
 *
 * @returns {void}
 */
const createCalendarEventWeb = (event) => {
  const isAllDay = event.start === event.end;

  const formatICSDateTime = (date) => new Date(date).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

  const formatICSDateOnly = (date) => {
    const d = new Date(date);
    return (
      d.getUTCFullYear().toString() +
      String(d.getUTCMonth() + 1).padStart(2, "0") +
      String(d.getUTCDate()).padStart(2, "0")
    );
  };

  const generateICS = () => {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);

    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//VBGo//Event//EN",
      "BEGIN:VEVENT",
      `UID:${Date.now()}@vbgo.app`,
      `DTSTAMP:${formatICSDateTime(new Date())}`,
    ];

    if (isAllDay) {
      lines.push(
        `DTSTART;VALUE=DATE:${formatICSDateOnly(startDate)}`,
        `DTEND;VALUE=DATE:${formatICSDateOnly(new Date(startDate.getTime() + 24 * 60 * 60 * 1000))}`
      );
    } else {
      lines.push(`DTSTART:${formatICSDateTime(startDate)}`, `DTEND:${formatICSDateTime(endDate)}`);
    }

    lines.push(
      `SUMMARY:${event.name}`,
      `DESCRIPTION:${stripHTML(event.description)}`,
      `LOCATION:${event.address}`,
      "END:VEVENT",
      "END:VCALENDAR"
    );

    return lines.join("\r\n");
  };

  const downloadICS = () => {
    const icsContent = generateICS();
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${event.name.replace(/[^a-z0-9]/gi, "_")}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const stripHTML = (html = "") => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("script, style").forEach((el) => el.remove());
    const text = doc.body.textContent || "";
    return text
      .replace(/&#x96;|&ndash;/g, "–")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  downloadICS();
};
